/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useCallback, useMemo, useState } from 'react';

import { FormattedMessage, FormattedNumber } from 'js/lib/coursera.react-intl';
import { getOptimizedSrcs } from 'js/lib/imgix';

import {
  Chip,
  ChipGroup,
  Grid,
  Link,
  PageGridContainer,
  SelectOption,
  Typography2,
  breakpoints,
  useMediaQuery,
} from '@coursera/cds-core';
import { ArrowNextIcon } from '@coursera/cds-icons';
import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import LazyImgix from 'bundles/page/components/shared/LazyImgix';
import RoundedSelectField from 'bundles/unified-common/components/RoundedSelectField';
import CareerOutcomesModuleRedesign from 'bundles/unified-description-page-common/components/modules/career-outcomes/CareerOutcomesModuleRedesign';
import Section from 'bundles/unified-description-page-common/components/shared/Section';
import xdpStyles from 'bundles/unified-description-page-common/constants/styles';
import { useCareerRolesData } from 'bundles/unified-description-page-common/contexts/CareerRolesDataContext';
import { useEventingProductData } from 'bundles/unified-description-page-common/contexts/EventingContext';
import { useXDPRedesignPhase1Data } from 'bundles/unified-description-page-common/providers/specialization/XDPRedesignPhase1Provider';
import type { SkillCareerRoleFragment as CareerRoleFragment } from 'bundles/unified-description-page-common/queries/__generated__/CareerRolesQuery';
import { SPECIALIZATION_NAV_ITEMS } from 'bundles/unified-description-page-common/utils/getPageNavigationLinks';

import careerOutcomesDataBoxImage from 'images/unified-description-page/career-outcomes-specialization.png';

import _t from 'i18n!nls/xdp';

interface Props {
  anchorId?: string;
  careerGrowthInPercentage?: number | null;
  careerRoles?: CareerRoleFragment[];
  valueProps?: string[];
}

const styles = {
  moduleContainer: (mobileImageUrl: string | null | undefined, enableXDPRedesignPhase1: boolean) => css`
    position: relative;
    z-index: 0;
    width: 100%;
    ${enableXDPRedesignPhase1 ? xdpStyles.border('border') : 'border: 2px solid var(--cds-color-grey-200);'}
    border-radius: var(--cds-border-radius-200);
    padding: var(--cds-spacing-300);
    overflow: hidden;

    ${breakpoints.down('sm')} {
      border-radius: calc(var(--cds-border-radius-200) + var(--cds-border-radius-50));
      padding: var(--cds-spacing-150);
      background-image: url(${mobileImageUrl});
      background-size: contain;
      background-repeat: no-repeat;
      background-position: 100% center;
    }

    // We need a little more space on the smallest devices to avoid
    // the image overlapping with the text.
    ${breakpoints.down(400)} {
      background-position: 110% center;
    }
  `,
  careerRolesSelect: css`
    margin-bottom: var(--cds-spacing-200);

    ${breakpoints.down('sm')} {
      margin-bottom: var(--cds-spacing-100);
    }
  `,
  contentContainer: css`
    width: 100%;
    box-sizing: border-box;
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
    padding-right: 40%;

    ${breakpoints.down('sm')} {
      padding-right: 33%;
    }
  `,
  valuePropsContainer: css`
    display: flex;
    width: 100%;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: var(--cds-spacing-200);

    ${breakpoints.down('sm')} {
      flex-direction: column;
      flex-wrap: wrap;
    }
  `,
  valuePropsItem: css`
    padding-right: var(--cds-spacing-200);

    & + & {
      margin-left: calc(var(--cds-spacing-200) * -1);
      padding-left: var(--cds-spacing-200);
      border-left: 1px solid var(--cds-color-neutral-stroke-primary-weak);
    }

    & + &:last-of-type {
      padding-right: 0;
    }
  `,
  imageContainer: css`
    display: block;
    position: absolute;
    top: 0;
    left: 65%;
    bottom: 0;
    max-width: 50%;

    ${breakpoints.down('sm')} {
      max-width: 30%;
      left: auto;
      right: 0;
    }
  `,
};

const MAX_NUMBER_OF_ROLE_CHIPS = 2;

const PathwaysCareerOutcomesModule = ({ anchorId, careerRoles, careerGrowthInPercentage, valueProps }: Props) => {
  const eventingProductData = useEventingProductData();
  const { eventingPathwaysData } = useCareerRolesData();
  const { enableXDPRedesignPhase1 } = useXDPRedesignPhase1Data();
  const track = useTracker();
  const visibilityTrackerRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_page_section',
    {
      pageSection: {
        sectionName: 'career_info',
      },
      ...eventingProductData,
    }
  );
  const careerRolesData = useMemo(() => careerRoles?.filter((role) => Boolean(role.name)), [careerRoles]);
  const [selectedRole, setSelectedRole] = useState<CareerRoleFragment | null>(careerRolesData?.[0] ?? null);
  const pathwaysDataBoxVisibilityTrackerRef: React.MutableRefObject<HTMLDivElement | null> = useVisibilityTracker(
    'view_role_card',
    {
      role: {
        roleSlug: selectedRole?.slug ?? '',
      },
      productCard: {
        index: careerRolesData?.findIndex((role) => role.id === selectedRole?.id) ?? 0,
        recommender: eventingPathwaysData?.recommender,
      },
      carousel: eventingPathwaysData?.carousel,
    }
  );
  const isMobile = useMediaQuery(breakpoints.down('sm'));
  let mobileImageUrl: string | undefined = '';
  if (selectedRole?.careerOutcomesModuleImageMobile?.url) {
    mobileImageUrl = getOptimizedSrcs(selectedRole?.careerOutcomesModuleImageMobile?.url, 164, 259, {
      crop: 'right',
      fit: 'fill',
      trim: 'alpha',
      trimAlpha: 0,
    }).dpr2;
  }

  const handleRoleFilterClick = (roleId: string) => {
    const targetRole = careerRolesData?.find((role) => role.id === roleId);
    setSelectedRole(targetRole || null);

    if (!targetRole) return;
    track('click_button', {
      button: {
        name: 'collections_filter_chip',
        linkName: `collections_filter_chip_${targetRole.slug.replaceAll('-', '_')}`,
      },
    });
  };

  const handleExploreRoleLinkClick = useCallback<() => $TSFixMe>(() => {
    if (!careerRolesData || !selectedRole) return;

    track('click_button', {
      button: {
        name: 'role_info',
        linkName: 'description_page_role_link',
      },
      ...(selectedRole.slug
        ? {
            role: {
              roleSlug: selectedRole.slug,
            },
          }
        : {}),
      productCard: {
        index: careerRolesData.findIndex((role) => role.id === selectedRole.id),
        recommender: eventingPathwaysData?.recommender,
      },
      carousel: eventingPathwaysData?.carousel,
    });
  }, [track, selectedRole, eventingPathwaysData, careerRolesData]);

  const renderRoleFilter = () => {
    if (!careerRolesData) return null;

    const numberOfRoles = careerRolesData.length;

    if (numberOfRoles <= 1) return null;

    if (numberOfRoles <= MAX_NUMBER_OF_ROLE_CHIPS) {
      return (
        <ChipGroup aria-label={_t('Career Roles')} css={styles.careerRolesSelect}>
          {careerRolesData.map((careerRole) => (
            <Chip
              key={careerRole.id}
              selected={selectedRole?.id === careerRole.id}
              onClick={() => handleRoleFilterClick(careerRole.id)}
            >
              {careerRole.name as string}
            </Chip>
          ))}
        </ChipGroup>
      );
    }

    return (
      <RoundedSelectField
        value={selectedRole?.id}
        aria-label={_t('Career Role Selector')}
        onChange={(e) => handleRoleFilterClick(e.target.value as string)}
        css={styles.careerRolesSelect}
      >
        {careerRolesData.map((careerRole) => (
          <SelectOption value={careerRole.id} key={careerRole.id}>
            {careerRole.name}
          </SelectOption>
        ))}
      </RoundedSelectField>
    );
  };

  if (!careerRolesData || !careerRolesData.length) return null;

  /**
   * This has been added to prevent the module rendering poorly when the offline data loads incorrectly.
   */
  const shouldRenderCareersFallback = () => {
    const { medianSalary, numJobsAvailable, countryName, currencyCode } = selectedRole?.jobPostingData ?? {};

    return ![medianSalary, numJobsAvailable, countryName, currencyCode].some(
      (value) => value !== null && value !== undefined
    );
  };

  const renderDataBox = () => {
    const { medianSalary, numJobsAvailable, countryName, currencyCode } = selectedRole?.jobPostingData ?? {};
    const jobTitle = selectedRole?.name;

    const items = [];

    if (medianSalary && jobTitle && currencyCode) {
      items.push({
        key: 'medianSalary',
        render: () => (
          <Typography2 component="span" variant="bodySecondary" color="supportText">
            <FormattedMessage
              message={
                isMobile
                  ? _t('{salary} median entry-level salary¹')
                  : _t('{salary} median entry-level salary for {jobTitle}¹')
              }
              salary={
                <Typography2 component="div" variant="subtitleLarge">
                  <FormattedNumber
                    value={medianSalary}
                    // eslint-disable-next-line react/style-prop-object
                    style="currency"
                    currency={currencyCode}
                    maximumFractionDigits={0}
                    minimumFractionDigits={0}
                  />
                </Typography2>
              }
              jobTitle={jobTitle}
            />
          </Typography2>
        ),
      });
    }

    if (numJobsAvailable !== null && numJobsAvailable !== undefined && jobTitle && countryName) {
      items.push({
        key: 'numJobsAvailable',
        render: () => (
          <Typography2 component="span" variant="bodySecondary" color="supportText">
            <FormattedMessage
              message={
                isMobile
                  ? _t('{numberOfJobs} job openings in {countryName}¹')
                  : _t('{numberOfJobs} job openings for {jobTitle}  in {countryName}¹')
              }
              countryName={countryName}
              numberOfJobs={
                <Typography2 component="div" variant="subtitleLarge">
                  <FormattedNumber value={numJobsAvailable} />
                </Typography2>
              }
              jobTitle={jobTitle}
            />
          </Typography2>
        ),
      });
    }

    if (careerGrowthInPercentage && currencyCode?.toUpperCase() === 'USD') {
      items.push({
        key: 'careerGrowthInPercentage',
        render: () => (
          <Typography2 component="span" variant="bodySecondary" color="supportText">
            <FormattedMessage
              message={_t('{careerGrowthInPercentage} of certificate grads report career improvement²')}
              careerGrowthInPercentage={
                <Typography2 component="div" variant="subtitleLarge">
                  {/* eslint-disable-next-line react/style-prop-object */}
                  <FormattedNumber value={careerGrowthInPercentage / 100} style="percent" />
                </Typography2>
              }
            />
          </Typography2>
        ),
      });
    }

    return (
      <div css={styles.valuePropsContainer} ref={pathwaysDataBoxVisibilityTrackerRef}>
        {items.map((itemRenderFunc) => (
          <div css={styles.valuePropsItem} key={itemRenderFunc.key}>
            {itemRenderFunc.render()}
          </div>
        ))}
      </div>
    );
  };

  // If the data is unavailable we render a slightly better Career outcomes module
  if (shouldRenderCareersFallback())
    return (
      <CareerOutcomesModuleRedesign
        anchorId={SPECIALIZATION_NAV_ITEMS.OUTCOMES}
        title={_t('Prepare for a career as a #{domainName}', { domainName: selectedRole?.name })}
        valueProps={valueProps}
        imageSrc={careerOutcomesDataBoxImage}
        cta={
          <Link
            variant="quiet"
            typographyVariant="subtitleMedium"
            href={`/career-academy/roles/${selectedRole?.slug}${
              eventingPathwaysData?.carousel?.id ? `?recommenderId=${eventingPathwaysData?.carousel?.id}` : ''
            }`}
            icon={<ArrowNextIcon />}
            iconPosition="after"
            onClick={handleExploreRoleLinkClick}
          >
            {_t('Explore this role')}
          </Link>
        }
      />
    );

  return (
    <Section anchorId={anchorId} ref={visibilityTrackerRef}>
      <PageGridContainer>
        {isMobile && (
          <Grid item xs={12}>
            {isMobile && renderRoleFilter()}
            <Typography2 component="h2" variant="subtitleLarge">
              {_t('Prepare for a career as a #{domainName}', { domainName: selectedRole?.name })}
            </Typography2>
          </Grid>
        )}
        <Grid item xs={12}>
          <div css={styles.moduleContainer(mobileImageUrl, enableXDPRedesignPhase1)}>
            {!isMobile && renderRoleFilter()}
            <div css={styles.contentContainer}>
              {!isMobile && (
                <Typography2 component="h2" variant="subtitleLarge">
                  {_t('Prepare for a career as a #{domainName}', { domainName: selectedRole?.name })}
                </Typography2>
              )}

              {renderDataBox()}

              <Link
                variant="quiet"
                typographyVariant="subtitleMedium"
                href={`/career-academy/roles/${selectedRole?.slug}${
                  eventingPathwaysData?.carousel?.id ? `?recommenderId=${eventingPathwaysData?.carousel?.id}` : ''
                }`}
                icon={<ArrowNextIcon />}
                iconPosition="after"
                onClick={handleExploreRoleLinkClick}
              >
                {_t('Explore this role')}
              </Link>
            </div>

            {!isMobile && selectedRole?.careerOutcomesModuleImage?.url && (
              <div css={styles.imageContainer}>
                <LazyImgix
                  src={selectedRole.careerOutcomesModuleImage?.url}
                  alt=""
                  maxHeight={317}
                  maxWidth={500}
                  imgParams={{ fit: 'fill', trim: 'alpha', trimAlpha: 0 }}
                />
              </div>
            )}
          </div>
        </Grid>
      </PageGridContainer>
    </Section>
  );
};

export default PathwaysCareerOutcomesModule;
